import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Box, useToast } from "@chakra-ui/react";
import {
  getFilmPrefaceData,
  updateFilmPrefaceData,
} from "../../../services/Films";
import Form from "@rjsf/chakra-ui";
import validator from "@rjsf/validator-ajv8";
// import SubmitButton from '../../../CommonComponents/SubmitButton';
import {
  getDeletedValues,
  reverseTransformData,
  transformData,
} from "../../../util/helper";
// import ImageWidget from '../../../CommonComponents/ImageWidget';
import CKEditorWidget from "../../../CommonComponents/CKEditorWidget";
import { RjsfSubmitButton } from "../../../RJSFCommonSchemas";
import FormLoader from "../../../CommonComponents/FormLoader";
import { showErrorToast, showSuccessToast } from "../../../util/errorHandler";

const Preface = () => {
  const { alias } = useParams();
  // const [basicFieldData, setBasicFieldData] = useState({});
  const [processedData, setProcessedData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [quickSave, setQuickSave] = useState(false);
  const toast = useToast();

  const extractPlainText = (htmlContent) => {
    if (!htmlContent) return "";
    const container = document.createElement("div");
    container.innerHTML = htmlContent;
    const text = container.textContent || container.innerText || "";
    return text
      .replace(/\s+\n/g, "\n")
      .replace(/\n{3,}/g, "\n\n")
      .trim();
  };

  // Trims each string and drops empty/whitespace-only entries — always
  // returns an array so the API always receives a clean payload.
  const cleanArray = (arr) =>
    (arr ?? []).map((v) => (v ?? "").trim()).filter((v) => v !== "");

  const schema = {
    title: "Film Preface fields",
    description: "",
    type: "object",
    properties: {
      short_description_tag: {
        type: "string",
        title: "Preface",
      },
      short_description_hindi: {
        type: "string",
        title: "Preface Hindi",
      },
      hyperlink_excluded_words: {
        type: "array",
        title: "Excluded Hyperlink Words",
        description:
          "Words or phrases that should never be auto-linked in this film's Preface (e.g. an ordinary word that happens to match an unrelated entity's name).",
        items: {
          type: "string",
        },
      },
      // Note: poster image carousel alias and other publicity image carousel alias are derived fields
      // "poster_image_carousel_alias": {
      //   "type": "array",
      //   "title": "Poster Image Carousel Alias",
      //   "items": {
      //   "type": "object",
      //   "properties": {
      //     "image_alias": {
      //       "type": "string",
      //       "title": "Image Alias",
      //       "default":""
      //     },
      //     "image": {
      //       "type": "string",
      //       "title": "Image",
      //       "default":""
      //     },
      //   }
      // }
      // },
      // "other_publicity_image_carousel_alias": {
      //   "type": "array",
      //   "title": "Other Publicity Image Alias Carousel",
      //   "items": {
      //   "type": "object",
      //   "properties": {
      //     "image_alias": {
      //       "type": "string",
      //       "title": "Image Alias",
      //       "default":""
      //     },
      //     "image": {
      //       "type": "string",
      //       "title": "Image",
      //       "default":""
      //     },
      //   }
      // }
      // }
    },
  };

  const uiSchema = {
    ...RjsfSubmitButton(isSubmitting),
    short_description_tag: {
      "ui:widget": CKEditorWidget,
      "ui:options": {
        placeholder: "Enter preface content...",
        height: "400px",
      },
    },
    short_description_hindi: {
      "ui:widget": CKEditorWidget,
      "ui:options": {
        placeholder: "Enter preface content in Hindi...",
        height: "400px",
      },
    },
    // Note: poster image carousel alias and other publicity image carousel alias are derived fields
    // "poster_image_carousel_alias": {
    //   items: {
    //     image: {
    //       "ui:widget": ImageWidget, // Use the custom TypeaheadWidget
    //     },
    //   },
    // },
    // "other_publicity_image_carousel_alias": {
    //   items: {
    //     image: {
    //       "ui:widget": ImageWidget, // Use the custom TypeaheadWidget
    //     },
    //   },
    // }
  };

  const formData = processedData;

  // Fetch initial data
  useEffect(() => {
    const getPrefaceData = async () => {
      try {
        setIsLoading(true);
        if (alias) {
          const result = await getFilmPrefaceData(alias);
          if (result) {
            const fieldsToTransform = [
              "poster_image_carousel_alias",
              "other_publicity_image_carousel_alias",
            ];

            const transformed = transformData(result, fieldsToTransform);
            setProcessedData({
              ...transformed,
              // Neo4j returns null (not []) when no exclusion words have been set yet.
              hyperlink_excluded_words: Array.isArray(
                transformed?.hyperlink_excluded_words,
              )
                ? transformed.hyperlink_excluded_words
                : [],
            });
            //setBasicFieldData(result);
          }
        }
      } catch (error) {
        console.error("Error fetching film data:", error);
        showErrorToast(toast, "Failed to fetch film data");
      } finally {
        setIsLoading(false);
      }
    };

    getPrefaceData();
  }, [alias, toast, quickSave]);

  // Submit updated data
  const handleSubmit = async (formData) => {
    // Ensure plain text is sent along with the HTML version
    const formDataWithPlain = {
      ...formData,
      short_description: extractPlainText(formData?.short_description_tag),
      hyperlink_excluded_words: cleanArray(formData?.hyperlink_excluded_words),
    };

    const fieldsToTransform = [
      "poster_image_carousel_alias",
      "other_publicity_image_carousel_alias",
    ];

    const updatedFormData = reverseTransformData(
      formDataWithPlain,
      fieldsToTransform,
    );

    // hyperlink_excluded_words is a Neo4j primitive-array field that the
    // backend only appends to — removed words must be sent via deletedValues.
    const allDeletedValues = getDeletedValues(processedData, updatedFormData);
    const deletedValues = {};
    if (allDeletedValues.hyperlink_excluded_words) {
      deletedValues.hyperlink_excluded_words =
        allDeletedValues.hyperlink_excluded_words;
    }
    // setIsSubmitting(true);

    try {
      if (alias) {
        setIsSubmitting(true);
        const result = await updateFilmPrefaceData(
          alias,
          updatedFormData,
          deletedValues,
        );

        if (result?.status === 200) {
          setQuickSave((prev) => !prev);
          showSuccessToast(toast, "Data updated successfully");
        }

        // Exit editing mode
        // setIsEditing(false);
      }
    } catch (error) {
      console.error("Error updating film data:", error);
      showErrorToast(toast, "Failed to update film data");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box maxW="1200px">
      {isLoading ? (
        <FormLoader />
      ) : (
        <Form
          schema={schema}
          uiSchema={uiSchema}
          formData={formData}
          validator={validator}
          onSubmit={(e) => handleSubmit(e.formData)}
        />
      )}
    </Box>
  );
};

export default Preface;
